import { FormEvent, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Camera,
  CameraOff,
  CheckCircle2,
  Clock,
  FileText,
  Globe,
  KeyRound,
  Loader2,
  Lock,
  Play,
  ShieldCheck,
  Wifi,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ApiError,
  clearAttemptId,
  clearStartNonce,
  emitEvent,
  fetchAttemptPosition,
  fetchIdentityCheckStatus,
  fetchPreview,
  loadAttemptId,
  loadCandidateMeta,
  markCameraEnabled,
  openIdentityCheck,
  resumeAttempt,
  saveAttemptId,
  saveCandidateMeta,
  startAttempt,
  startNonce,
  type IdentityCheckView,
  type PreviewResponse,
} from "@/lib/api";
import { checkBrowserCompat } from "@/lib/browserCompat";
import { EMAIL_RE } from "@/lib/csv";
import { captureSilentFailure } from "@/lib/observability";
import { useStillWaiting } from "@/lib/useStillWaiting";
import {
  classifyQuestionError,
  describeStartError,
  screeningDeclinedMessage,
} from "@/lib/candidateError";
import { probeExtensionTraces } from "@/anticheat/extensions";
import { probeAutomationTraits } from "@/anticheat/automation"; // FEAT-38
import { EMAIL_MAX_LENGTH, cn, formatDateTime, formatHMS } from "@/lib/utils";
import {
  intakeInputAttrs,
  intakeValueError,
  type IntakeField,
} from "@/lib/candidateIntake";
import { preloadMonaco } from "@/lib/monaco";
import { getRuntime, isServerLanguage, languageOf, SANDBOX_NAME } from "@/lib/runtimes";
import { queryKeys } from "@/lib/queryKeys";
import {
  anticheatAllOff,
  checksFromPreview,
  resolveAnticheat,
  type AnticheatConfig,
} from "@/lib/anticheatConfig";
import {
  describeCameraDevice,
  disposeActiveStream,
  loadModels,
  requestCameraStream,
  type CameraStreamError,
} from "@/anticheat/camera";
import { isFullscreenSupported, requestFullscreen } from "@/anticheat/fullscreen";
import { probeCursorTracking, probeSecondScreen } from "@/anticheat";
import {
  canProbeDisplayPlacement,
  probeDisplayPlacement,
  probeFormFactor,
} from "@/anticheat/displays";
import { BrowserSetupStep } from "@/components/candidate/BrowserSetupStep";
import { TestRulesStep } from "@/components/candidate/TestRulesStep";
import { buildTestRules } from "@/lib/testRules";
import { IdentityStep } from "@/components/candidate/IdentityStep";
import {
  ScreeningDeclinedCard,
  ScreeningStep,
} from "@/components/candidate/ScreeningStep";
import { CameraPreview } from "@/components/candidate/CameraPreview";
import {
  CAMERA_TILE_RESERVE,
  useCameraTileShown,
} from "@/components/candidate/cameraTile";
import { CodeEditorInstructions } from "@/components/candidate/CodeEditorInstructions";
import { ConnectionPreflight } from "@/components/candidate/ConnectionPreflight";
import { ConsentCheckbox } from "@/components/candidate/ConsentCheckbox";
import { Disclosure } from "@/components/candidate/Disclosure";
import type { CameraDenyReason } from "@/anticheat/events";

// EF-2: what the "Welcome back" card and the resume chain need. Comes from
// the Paused screen's router state (same device) or from `/preview.resume`
// (a fresh device opening the same invitation link).
interface ResumeCardInfo {
  position: number;
  resumeBy: string | null;
  remainingSeconds: number | null;
}

// CP-18 (Sheet #29): an invitation link reopened while its attempt is ON A
// BREAK between sections — the tab that held the attempt id is gone. Comes
// from `/preview.break_return`; the fields are what the break screen
// (SectionReview) needs as router state, plus the two lines the card shows.
interface BreakReturnInfo {
  sectionIndex: number;
  sectionStart: number;
  sectionEnd: number;
  isLast: boolean;
  sharedClock: boolean;
  remainingSeconds: number | null;
}

type Step =
  | { kind: "welcome" }
  // FEAT-16: the test rules — allowed, not allowed, what is monitored, the
  // consequence. Right after Welcome, so it is read before anything is asked.
  | { kind: "rules" }
  // EF-4: the browser-setup step — instructions + the acknowledgement that
  // /start records. A page cannot see extensions, so this is an honour step.
  | { kind: "setup" }
  | { kind: "form" }
  // EP-20: the recruiter's knock-off questions, AFTER the form (consent first)
  // and BEFORE /start — so before the camera, fullscreen and the clock.
  | { kind: "screening" }
  // EF-3: the optional identity check (ID + selfie on the vendor's page, in a
  // new tab). AFTER the form and screening, BEFORE /start — so the clock is
  // not running and this tab's form state is still alive. Never blocks.
  | { kind: "identity" }
  // EP-20: a knock-off answer ended the session. Terminal, and deliberately
  // not a route: no attempt id is handed out, so there is nothing for Done to
  // poll, and nothing in router state to survive a reload.
  | { kind: "declined"; message: string }
  | { kind: "unsupported"; missing: string[] }
  // EF-2: a paused attempt. `resume` then rides the camera / denied / ready
  // steps so the final Begin gesture calls POST /resume (the clock restarts
  // there, after the camera is back) instead of opening question 1.
  | { kind: "resume"; attemptId: string; info: ResumeCardInfo }
  | { kind: "resume_expired"; message: string }
  // CP-18: back on an invitation link while the test is on a break. The card
  // leads to the camera step (when this attempt has one) and then straight to
  // the break screen — whose own "Begin Section N" is the gesture that
  // restarts the clock, so there is no `ready` step on this path.
  | { kind: "break_return"; attemptId: string; info: BreakReturnInfo }
  | {
      kind: "camera";
      attemptId: string;
      requesting: boolean;
      resume?: ResumeCardInfo;
      breakReturn?: BreakReturnInfo;
    }
  | {
      kind: "denied";
      attemptId: string;
      reason: CameraDenyReason;
      resume?: ResumeCardInfo;
      breakReturn?: BreakReturnInfo;
    }
  | { kind: "ready"; attemptId: string; resume?: ResumeCardInfo };

/**
 * Run a callback during browser idle time so it doesn't compete with the
 * user's first typing window. Falls back to `setTimeout(cb, 500)` on Safari
 * 16 and older (no `requestIdleCallback`). Returns a cleanup function
 * suitable for `useEffect`'s return slot.
 *
 * Used to defer the heavy face-api + Pyodide preloads (which compile WASM on
 * the main thread) until after the form's `<Input>` elements have mounted
 * and the candidate's first keystrokes can land.
 */
// OPS-28 #13: POST /camera-enabled with the answer queue's first rungs. The
// flag is what the recruiter's "Camera permission" chip reads, and a single
// lost POST used to show "Off" for a camera that ran. 409/410 (the attempt is
// over) and 4xx stop at once; a blip, a 5xx or a 429 retry; spent retries
// reach Sentry with the status only.
const MARK_CAMERA_BACKOFF_MS = [1000, 3000, 8000];
async function markCameraEnabledDurably(
  token: string,
  attemptId: string,
): Promise<void> {
  for (let i = 0; ; i++) {
    try {
      await markCameraEnabled(token, attemptId);
      return;
    } catch (err) {
      const status = err instanceof ApiError ? err.status : null;
      const retryable = status === null || status >= 500 || status === 429;
      if (!retryable || i >= MARK_CAMERA_BACKOFF_MS.length) {
        captureSilentFailure("camera_enabled_flag_lost", {
          status: status ?? "network",
          tries: i + 1,
        });
        return;
      }
      await new Promise<void>((r) => setTimeout(r, MARK_CAMERA_BACKOFF_MS[i]));
    }
  }
}

function scheduleIdle(cb: () => void, timeoutMs = 2000): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(cb, { timeout: timeoutMs });
    return () => window.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(cb, 500);
  return () => window.clearTimeout(handle);
}

export default function Gate() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // MVP 2.0 #7: when the candidate completes (or skips) the practice prelude
  // at /t/<token>/practice, it navigates back here with { state: { from:
  // "practice" } }. We skip the Welcome step and drop straight into the form
  // so the candidate doesn't read the same intro card twice. State is wiped
  // on browser refresh, which is correct — refresh = restart from Welcome.
  const fromPractice =
    (location.state as { from?: string } | null)?.from === "practice";
  // A mid-test route bounced here because it could not place the candidate
  // (a locked section, a missing section context, a sectioned attempt on the
  // global review URL). Those routes bounce PRECISELY when they're stuck, so
  // auto-resuming back into them would ping-pong forever — Gate -> /question/1
  // -> section_locked -> Gate. Marked bounces therefore fall through to the
  // normal gate, which is exactly the behaviour those paths had before the
  // resume existed. See the resume note below.
  const bouncedBack =
    (location.state as { from?: string } | null)?.from === "bounce";
  // EF-2: the Paused screen's "Resume now" lands here with the pause facts in
  // router state. Like `bouncedBack` it must NOT auto-redirect to question 1
  // (that would 409 straight back to the paused screen); it opens the
  // "Welcome back" card instead, whose Begin gesture calls /resume.
  const resumeState = (() => {
    const s = location.state as
      | { from?: string; position?: unknown; resumeBy?: unknown; remainingSeconds?: unknown }
      | null;
    if (s?.from !== "resume" || typeof s.position !== "number") return null;
    return {
      position: s.position,
      resumeBy: typeof s.resumeBy === "string" ? s.resumeBy : null,
      remainingSeconds:
        typeof s.remainingSeconds === "number" ? s.remainingSeconds : null,
    } satisfies ResumeCardInfo;
  })();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // CP-12: the company's configurable sign-in fields (phone, years of
  // experience, …), keyed by field key, every value the raw input string.
  // `intakeTouched` gates the inline error so a field doesn't complain before
  // the candidate has reached it; Submit is gated regardless (see canSubmit).
  const [intake, setIntake] = useState<Record<string, string>>({});
  const [intakeTouched, setIntakeTouched] = useState<Set<string>>(
    () => new Set(),
  );
  // EP-20: the candidate's choice per screening question (key → option text).
  const [screening, setScreening] = useState<Record<string, string>>({});
  // Three mandatory pre-test consents. All must be ticked to enable "Start the
  // test"; the acceptance is recorded server-side at /start (consent_accepted).
  const [agreed, setAgreed] = useState(false);
  const [consentProctoring, setConsentProctoring] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // EF-2: the resume chain's in-flight flag + its retryable failure copy.
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  // CP-16: a start refusal the candidate fixes by editing the Email field
  // (`email_undeliverable` — the domain refuses or cannot receive mail),
  // rendered inline under the input instead of in the generic box, and
  // cleared the moment the field changes.
  const [emailError, setEmailError] = useState<string | null>(null);
  // Whether the candidate has finished with the field — the same rule the
  // intake fields below use ("errors show once a field has been left"), plus
  // the browser's own `invalid` event, which is what fires when `type=email`
  // blocks the submit. Without that second trigger, someone who types a bad
  // address and presses Enter without ever leaving the field gets the native
  // bubble and NOTHING in the DOM: `aria-invalid` stayed "false", there was no
  // `aria-describedby`, and a screen-reader user had only the transient bubble
  // to go on (2026-09-21 design review).
  const [emailTouched, setEmailTouched] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  // EF-4: the browser-setup acknowledgement, ticked on the `setup` step and
  // sent to /start as `extensions_confirmed`. The probe result is captured on
  // that same step (right before the form) and rides `attempt_started`.
  const [extensionsConfirmed, setExtensionsConfirmed] = useState(false);
  const extensionProbeRef = useRef<ReturnType<typeof probeExtensionTraces> | null>(
    null,
  );
  const [step, setStep] = useState<Step>(() => {
    // FEAT-25: the camera API counts only for a test whose camera check is on.
    // At mount that is known only for a live attempt (its FROZEN copy, read the
    // same way as below); every other path learns it from /preview, and the
    // effect after the preview query asks again then.
    const compat = checkBrowserCompat({
      camera:
        !bouncedBack &&
        loadAttemptId(token) != null &&
        loadCandidateMeta(token)?.anticheat?.camera !== false,
    });
    if (!compat.supported)
      return { kind: "unsupported", missing: compat.missing };
    // EF-2: back from the Paused screen with a live handle → the resume card.
    // Without a handle (storage cleared) fall through: an invitation link's
    // /preview offers the same card from the server's own record.
    const stored = resumeState ? loadAttemptId(token) : null;
    if (resumeState && stored) {
      return { kind: "resume", attemptId: stored, info: resumeState };
    }
    // FRONTEND-REACT-8 resume (see `resumeAttemptId` below for the rules): a
    // live handle at mount re-enters at the CAMERA step, not at question 1
    // (2026-09-18). Going straight to the question made a reload on the
    // "Camera access is required" card a bypass — the candidate landed on
    // question 1 with the camera never granted, CP-14 tried once more there
    // and gave up, and the recruiter read "denied" for a device another app
    // was holding. The consent → camera → Begin chain re-establishes the
    // camera AND fullscreen; the landing is still question 1, as before.
    const live = bouncedBack ? null : loadAttemptId(token);
    // FEAT-25: an attempt that froze the camera check OFF has no camera to
    // re-establish. It lands on the Begin card instead — still a rendered
    // step with its own gesture, which is what re-enters fullscreen.
    if (live && loadCandidateMeta(token)?.anticheat?.camera === false) {
      return { kind: "ready", attemptId: live };
    }
    if (live) return { kind: "camera", attemptId: live, requesting: false };
    // Back from the practice prelude: skip the Welcome card they already read,
    // but NOT the browser-setup step (EF-4) — it comes after Welcome, so a
    // candidate who went to practice from Welcome has not seen it yet.
    // FEAT-16: nor the rules, which come first of the two.
    return fromPractice ? { kind: "rules" } : { kind: "welcome" };
  });

  // RESUME (FRONTEND-REACT-8, 2026-08-25). Landing on the gate with a live
  // in-flight attempt in this tab means the candidate already POSTed /start and
  // came back — a crash caught by the route ErrorBoundary whose only affordance
  // is "Reload page", or a plain refresh. Re-gating them was a DEAD END: /start
  // consumes a per-invitation link, so /preview answers 410 "already been used"
  // (and a shared per-test link 409s "already in progress"). The candidate lost
  // the assessment mid-flight and only a recruiter-granted retake got it back.
  //
  // Question/Review/SectionReview have always resumed from this same key; the
  // gate was the one route that didn't. Send them back into the running attempt
  // instead. Safe: the server re-binds attempt->token on every mid-test route
  // (_get_attempt_for_token, F3), so a forged id buys nothing.
  //
  // Read ONCE at mount, via the useState initializer — NOT reactively. The gate
  // stays mounted through the camera/ready steps AFTER saveAttemptId() runs at
  // /start, so a live read would bounce the candidate out of the camera step in
  // the normal flow. Done.tsx clears the key on submit, so a finished candidate
  // returning here falls through to the normal 409 "already completed".
  //
  // WHERE it lands (2026-10-10): Begin asks the server (`GET /position`, see
  // beginAssessment) — the furthest question opened in the section the
  // candidate is in now. Until then it always opened question 1, which for
  // anyone past section 1 is a LOCKED section: 409 → the bounce below → the
  // normal gate on a consumed link, i.e. locked out of a running test.
  // `bouncedBack` still never resumes: a bounce means a mid-test route could
  // not place the candidate, and the rule stays uniform (see the memory).
  // EF-2: a `from: "resume"` arrival is handled by the resume card above,
  // never by this auto-redirect (question 1 would 409 `attempt_paused`).
  // The redirect itself moved into the `step` initializer above (the camera
  // step); this handle gates the /preview fetch below, the camera card's
  // wording and Begin's server-placed landing. It must NEVER short-circuit the
  // render: an early "neutral background" return survived that move and left
  // a returning candidate on a blank page until 2026-09-30. Its setter only
  // ever CLEARS it (a handle the server does not recognise), which re-enables
  // /preview for the normal gate.
  const [resumeAttemptId, setResumeAttemptId] = useState(() =>
    bouncedBack || resumeState ? null : loadAttemptId(token),
  );

  // Preview is fetched eagerly so the Welcome screen has its data on first paint.
  // It's read-only and does NOT create an attempt row — the timer starts on form submit.
  // Skipped entirely while resuming: on a consumed invitation it would only 410,
  // painting a "link already used" error over a candidate we're about to redirect.
  const preview = useQuery<PreviewResponse>({
    queryKey: queryKeys.preview(token),
    queryFn: () => fetchPreview(token),
    staleTime: 60_000,
    enabled: step.kind !== "unsupported" && !resumeAttemptId,
  });
  // EF-8: the snapshot schedule this attempt will run under, or null. The
  // consent wording below switches on it — a candidate must be told BEFORE
  // agreeing that photos are taken and kept. OPS-15: legal supplies the
  // final wording of every snapshot variant; these are placeholders.
  const snaps = preview.data?.camera_snapshots ?? null;
  // FEAT-25: the recruiter can switch the camera check, fullscreen and
  // activity monitoring off per test. BEFORE /start the Gate reads /preview's
  // live copy (which steps and sentences to show); AFTER it, and on every
  // resume, it reads the value the attempt FROZE — cached beside the attempt
  // id, because a mid-test reload fetches no /preview at all. A recruiter edit
  // between the two can therefore never split the camera step from the test.
  const previewChecks = resolveAnticheat(preview.data?.anticheat);
  // FEAT-25: the mount check above left the camera API out unless a live
  // attempt said its camera check is on; /preview says it for everyone else.
  const previewWantsCamera = preview.data != null && previewChecks.camera;
  useEffect(() => {
    if (step.kind === "unsupported" || !previewWantsCamera) return;
    const compat = checkBrowserCompat({ camera: true });
    if (!compat.supported) setStep({ kind: "unsupported", missing: compat.missing });
  }, [step.kind, previewWantsCamera]);
  const frozenChecks = (): AnticheatConfig =>
    resolveAnticheat(loadCandidateMeta(token)?.anticheat);
  // Every check off ⇒ the browser-setup step ("turn extensions off") has
  // nothing to protect and is skipped. Only once /preview has said so.
  const setupSkipped = preview.data != null && anticheatAllOff(previewChecks);
  useEffect(() => {
    // The render below already falls through to the form when the setup step
    // is skipped; this just names the step.
    if (step.kind === "setup" && setupSkipped) setStep({ kind: "form" });
  }, [step.kind, setupSkipped]);
  // FEAT-16: the rules are built from THIS test's switches, so there are none
  // until /preview has answered — a default would announce checks the
  // recruiter may have switched off. (Sheet #151: Emilia's wording no longer
  // varies with back navigation, pausing or the clock.)
  const testRules = preview.data
    ? buildTestRules({
        checks: previewChecks,
        hasCode: Boolean(preview.data.has_code),
        // FEAT-31: said on the rules step BEFORE the candidate starts.
        codeReplay: Boolean(preview.data.code_replay),
      })
    : null;
  // FEAT-16: a practice return lands on the rules; if /preview then fails,
  // Welcome is the step that says so.
  useEffect(() => {
    if (step.kind === "rules" && preview.isError) setStep({ kind: "welcome" });
  }, [step.kind, preview.isError]);
  // EF-3: this link's test offers the identity check (the server ANDs the
  // test's switch, the platform and the company's operator switch — the same
  // conjunction /start uses). False for every test today.
  const identityRequired = preview.data?.identity_verification === true;
  const [identity, setIdentity] = useState<IdentityCheckView>({
    status: "not_started",
    url: null,
  });
  const [identityOpening, setIdentityOpening] = useState(false);
  const [identityOpenError, setIdentityOpenError] = useState<string | null>(
    null,
  );
  const onIdentityStep = step.kind === "identity";
  const identitySettled =
    identity.status === "completed" || identity.status === "unavailable";
  // While the identity step is on screen, ask the server where the check
  // stands: once on arrival (a reload, or coming back from the form), then
  // whenever this tab becomes visible again — which is exactly when the
  // candidate returns from the vendor's tab — plus a slow tick as a backstop.
  // Nothing polls while the tab is hidden, and it stops once settled.
  useEffect(() => {
    if (!onIdentityStep || identitySettled) return;
    let cancelled = false;
    const poll = () => {
      fetchIdentityCheckStatus(token, {
        name: name.trim(),
        email: email.trim(),
      })
        .then((view) => {
          if (cancelled) return;
          // The URL only ever comes from the open call; keep it while the
          // check can still be done so the link stays on the card. A poll
          // that was already in flight when the session was opened can still
          // answer "not started" — it must not take the link away again.
          setIdentity((prev) =>
            view.status === "not_started" && prev.url !== null
              ? prev
              : {
                  status: view.status,
                  url: view.status === "pending" ? prev.url : null,
                },
          );
        })
        .catch(() => {
          // A failed poll changes nothing: the step never blocks, and the
          // next tick (or the candidate's own Continue) carries on.
        });
    };
    const pollIfVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    poll();
    const tick = window.setInterval(pollIfVisible, 5000);
    document.addEventListener("visibilitychange", pollIfVisible);
    window.addEventListener("focus", pollIfVisible);
    return () => {
      cancelled = true;
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", pollIfVisible);
      window.removeEventListener("focus", pollIfVisible);
    };
  }, [onIdentityStep, identitySettled, token, name, email]);

  const openIdentity = async () => {
    setIdentityOpening(true);
    setIdentityOpenError(null);
    // The optional camera check on the Welcome step may still hold the
    // webcam, and the vendor's page needs it. The camera step after /start
    // asks for it again.
    disposeActiveStream();
    try {
      setIdentity(
        await openIdentityCheck(token, {
          name: name.trim(),
          email: email.trim(),
        }),
      );
    } catch {
      setIdentityOpenError(
        "We couldn't open the identity check. Check your connection and try again.",
      );
    } finally {
      setIdentityOpening(false);
    }
  };

  // Per-invitation links (MVP 2.0 #4 invitation token resolves via Invitation
  // table fallback) carry the recruiter-registered identity on the preview
  // payload. Seed the form once, then lock the inputs so the candidate can't
  // submit as a different person. Rising-edge ref guards against a background
  // refetch clobbering values (defense-in-depth — the locked inputs shouldn't
  // ever edit). Memory: project_react_default_array_loop_gotcha.
  const isInvitationLink = !!preview.data?.invited_candidate_email;
  const seededFromInvitationRef = useRef(false);
  useEffect(() => {
    if (seededFromInvitationRef.current) return;
    if (
      preview.data?.invited_candidate_name &&
      preview.data?.invited_candidate_email
    ) {
      setName(preview.data.invited_candidate_name);
      setEmail(preview.data.invited_candidate_email);
      seededFromInvitationRef.current = true;
    }
  }, [
    preview.data?.invited_candidate_name,
    preview.data?.invited_candidate_email,
  ]);

  // EF-2, the fresh-device path: the same invitation link opened elsewhere
  // (or after the tab was closed) finds no stored handle, and /preview hands
  // the PAUSED attempt back instead of the "already been used" 410. Adopt the
  // server's handle + meta and open the resume card. Rising-edge ref so a
  // background refetch can't yank a candidate out of the camera/ready steps.
  const resumeSeededRef = useRef(false);
  const previewResume = preview.data?.resume ?? null;
  const inResumeChain =
    step.kind === "resume" ||
    step.kind === "resume_expired" ||
    step.kind === "break_return" ||
    step.kind === "camera" ||
    step.kind === "denied" ||
    step.kind === "ready";
  useEffect(() => {
    if (!previewResume || resumeSeededRef.current) return;
    // A same-device arrival (`from: "resume"`) is already in the chain from
    // router state; a late /preview must not yank it back to the card.
    if (inResumeChain) return;
    resumeSeededRef.current = true;
    saveAttemptId(token, previewResume.attempt_id);
    saveCandidateMeta(token, {
      candidate_name: previewResume.candidate_name,
      test_name: preview.data?.test_name ?? "",
      // FEAT-25: on the resume path /preview carries the FROZEN checks —
      // and (EF-8) the frozen snapshot schedule the review pages read.
      anticheat: resolveAnticheat(preview.data?.anticheat),
      // FEAT-38: /preview's live answer, as at /start — without it the fold
      // never arms again for the rest of the attempt.
      interaction_context: preview.data?.interaction_context === true,
      camera_snapshots: preview.data?.camera_snapshots ?? null,
    });
    setStep({
      kind: "resume",
      attemptId: previewResume.attempt_id,
      info: {
        position: previewResume.position,
        resumeBy: previewResume.resume_by,
        remainingSeconds: previewResume.remaining_seconds,
      },
    });
  }, [
    previewResume,
    preview.data?.test_name,
    preview.data?.anticheat,
    token,
    inResumeChain,
  ]);

  // CP-18 (Sheet #29), the same hand-back for a BREAK: the candidate closed
  // the tab on the break screen and opened the invitation link again. Shares
  // the rising-edge ref with the pause path — `/preview` sets one or the
  // other, never both.
  const previewBreak = preview.data?.break_return ?? null;
  useEffect(() => {
    if (!previewBreak || resumeSeededRef.current) return;
    if (inResumeChain) return;
    resumeSeededRef.current = true;
    saveAttemptId(token, previewBreak.attempt_id);
    saveCandidateMeta(token, {
      candidate_name: previewBreak.candidate_name,
      test_name: preview.data?.test_name ?? "",
      // On this path /preview carries the FROZEN checks, as for a resume.
      anticheat: resolveAnticheat(preview.data?.anticheat),
      interaction_context: preview.data?.interaction_context === true, // FEAT-38
      camera_snapshots: preview.data?.camera_snapshots ?? null,
    });
    setStep({
      kind: "break_return",
      attemptId: previewBreak.attempt_id,
      info: {
        sectionIndex: previewBreak.section.index,
        sectionStart: previewBreak.section.start_position,
        sectionEnd: previewBreak.section.end_position,
        isLast: previewBreak.section.is_last,
        sharedClock: previewBreak.shared_clock,
        remainingSeconds: previewBreak.remaining_seconds ?? null,
      },
    });
  }, [
    previewBreak,
    preview.data?.test_name,
    preview.data?.anticheat,
    token,
    inResumeChain,
  ]);

  // Preload face-api models while the candidate is reading Welcome / filling
  // the form. ~190 KB, idempotent singleton — by the time the camera step
  // runs, monitor.start() awaits an already-resolved promise instead of
  // freezing first paint on /question/1. Deferred to idle so the WASM/JSON
  // parse doesn't compete with the candidate's first keystrokes on the form.
  // FEAT-25: only for a test whose camera check is on — the frozen answer on
  // a mid-test reload (no /preview there), /preview's otherwise.
  const cameraWanted = resumeAttemptId
    ? resolveAnticheat(loadCandidateMeta(token)?.anticheat).camera
    : previewWantsCamera;
  useEffect(() => {
    if (step.kind === "unsupported" || !cameraWanted) return;
    return scheduleIdle(() => void loadModels().catch(() => {}));
  }, [step.kind, cameraWanted]);

  // Preload Monaco on the same idle window when the test has a code question.
  // ~0.3 MB self-hosted AMD bundle (/monaco-v3/vs) + editor.main parse. Without
  // this, Monaco only starts downloading when the first <Editor> mounts — after
  // the runtime has already loaded — so the first code question stalls ~570 ms
  // (prod 1–2 s+) on the "Loading … sandbox…" skeleton. loader.init() is
  // idempotent and SHARES its download with the <Editor> mount, so the editor
  // then resolves instantly. Same has_code + post-Welcome intent gate as the
  // Pyodide preload below; placed first so the smaller asset kicks off first.
  useEffect(() => {
    if (!preview.data?.has_code) return;
    if (step.kind === "welcome" || step.kind === "unsupported") return;
    return scheduleIdle(() => void preloadMonaco().catch(() => {}));
  }, [preview.data?.has_code, step.kind]);

  // Preload Pyodide on the same idle window when the test contains a code
  // question. ~12 MB CDN payload + WASM init takes 3–10 s; doing it during
  // Gate means Question.tsx's getPyodide() call resolves instantly.
  //
  // Gated to fire only after the candidate clicks Continue from Welcome —
  // i.e. they've signalled intent to take the test. Avoids burning 12 MB on
  // visitors who land and bounce without committing. Then deferred to idle
  // so the WASM compile doesn't lock the main thread the moment the form
  // mounts (which would block the candidate from typing their name + email).
  //
  // FEAT-6: only the runtimes this test's code questions need — a JavaScript-
  // or SQL-only test never pays Pyodide's 12 MB. An older server that sends no
  // `code_languages` means Python, as before.
  const codeLanguages = preview.data?.has_code
    ? (preview.data.code_languages?.length ? preview.data.code_languages : ["python"])
    : [];
  const codeLanguagesKey = codeLanguages.join(",");
  useEffect(() => {
    if (!codeLanguagesKey) return;
    if (step.kind === "welcome" || step.kind === "unsupported") return;
    return scheduleIdle(() => {
      for (const lang of codeLanguagesKey.split(",")) {
        void getRuntime(languageOf([lang])).catch(() => {});
      }
    });
  }, [codeLanguagesKey, step.kind]);

  // CP-12: the recruiter-configured extra fields, in the recruiter's order.
  // Config only — served by /preview, so a company that asks nothing renders
  // exactly the form it always did.
  const intakeFields: IntakeField[] = preview.data?.intake_fields ?? [];
  // EP-20: the test's knock-off questions — question + answers only; the
  // answer key never reaches this page. [] = the test screens nobody.
  const screeningQuestions = preview.data?.screening_questions ?? [];

  // All three consents ticked + name/email present + every extra field
  // passing the same per-type rule the server applies. Single source of truth
  // for both the submit handler's guard and the button's disabled state, so
  // the two can't drift (`submitting` is folded into the button's disabled only).
  // CP-16's server refusal wins over the format complaint: it is the more
  // specific thing to say about the same field.
  const emailFormatError =
    emailTouched && email.trim() !== "" && !EMAIL_RE.test(email.trim())
      ? "Enter a valid email address, like ada@example.com."
      : null;
  const shownEmailError = emailError ?? emailFormatError;

  const canSubmit =
    !!name.trim() &&
    !!email.trim() &&
    agreed &&
    consentProctoring &&
    acceptTerms &&
    intakeFields.every(
      (f) => intakeValueError(f, intake[f.key] ?? "") === null,
    );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    // `type=email` lets a dotless domain through ("ada@gmail"), so the native
    // check does not stop this submit — and the server's EmailStr refuses it
    // with a 422 that reads as "Could not start the test. Please try again.",
    // a retry that can never work, under a field already saying the address
    // is invalid (2026-09-27 verify). Refuse here instead, in the field's own
    // words. Not in `canSubmit`: a disabled Start button would also block the
    // native Enter-key validation that fires `onInvalid`.
    if (!EMAIL_RE.test(email.trim())) {
      setEmailTouched(true);
      emailInputRef.current?.focus();
      return;
    }
    setError(null);
    setEmailError(null);
    // EP-20: a screening test asks its knock-off questions next; /start runs
    // from that step's Continue, with the form's answers still in state.
    if (screeningQuestions.length > 0) {
      setStep({ kind: "screening" });
      return;
    }
    await startOrIdentity();
  };

  // EF-3: the last hop before /start. A test that offers the identity check
  // shows its step first; /start then runs from that step's Continue (or its
  // decline route). Every other test starts here, exactly as before.
  const startOrIdentity = async () => {
    if (identityRequired) {
      setError(null);
      setStep({ kind: "identity" });
      return;
    }
    await runStart();
  };

  const runStart = async (identityDeclined?: boolean) => {
    setSubmitting(true);
    setError(null);
    setEmailError(null);

    try {
      const result = await startAttempt(token, {
        name,
        email,
        consent_accepted: true,
        // EF-4: the browser-setup acknowledgement (the setup step's Continue
        // is disabled until it is ticked). FEAT-25: a test with every check
        // off skips that step, so the tick was never offered — the key is then
        // OMITTED, never sent as `false` (the server refuses an explicit
        // false; it stamps nothing for an absent one).
        ...(extensionsConfirmed ? { extensions_confirmed: true } : {}),
        // S1: the same nonce on every retry of this tab's start, so a click
        // whose response was lost gets its attempt back (and nobody else can).
        start_nonce: startNonce(token),
        // EF-3: only on a test that offers the check, so every other
        // tenant's wire body is byte-identical. True = "I don't want to do
        // this check"; it never blocks the start.
        ...(identityRequired
          ? { identity_declined: identityDeclined === true }
          : {}),
        // Every RENDERED key is sent, blank or not — that is how the server
        // tells "left empty" from "this form never showed the field" (a
        // required field added after the page loaded → a reload message).
        // Omitted entirely when nothing is configured, so the wire body of a
        // pre-CP-12 tenant is byte-identical.
        ...(intakeFields.length > 0
          ? {
              intake: Object.fromEntries(
                intakeFields.map((f) => [f.key, intake[f.key] ?? ""]),
              ),
            }
          : {}),
        // EP-20: every question's chosen option (Continue is disabled until
        // each has one). Omitted when the test screens nobody, so every other
        // tenant's wire body is byte-identical.
        ...(screeningQuestions.length > 0
          ? {
              screening: Object.fromEntries(
                screeningQuestions.map((q) => [q.key, screening[q.key] ?? ""]),
              ),
            }
          : {}),
      });
      saveAttemptId(token, result.attempt_id);
      clearStartNonce(token);
      // FEAT-25: from here on the FROZEN checks decide (camera step,
      // fullscreen request, what Question arms) — not /preview's live copy.
      const checks = resolveAnticheat(result.anticheat);
      // FEAT-38: /preview says whether interaction context is available
      // (the platform flag ∧ the legal gate — never frozen at /start).
      // Cached for Question's controller; the server drops the
      // summaries anyway when it is not.
      const interactionAvailable = preview.data?.interaction_context === true;
      saveCandidateMeta(token, {
        candidate_name: name.trim(),
        test_name: result.test_name,
        anticheat: checks,
        interaction_context: interactionAvailable, // FEAT-38
        // EF-8: the frozen snapshot schedule, for the review pages' hold +
        // caption (GET /review carries none).
        camera_snapshots: result.camera_snapshots ?? null,
      });

      // Fullscreen is NOT requested on this form-submit click. requestFullscreen()
      // needs a fresh user gesture AND no pending permission prompt — and the
      // camera prompt comes next, which would steal the fullscreen state (this
      // is why early entry on Welcome/form/camera-grant was reverted). Instead
      // it's requested on the later "Begin assessment" gesture (the "ready"
      // step, once the camera is already granted) — see beginAssessment.
      // Question 1 then mounts already-fullscreen; its blocking overlay
      // re-locks on any later exit. See Question.tsx.

      // Report detection capability once, up front, so the recruiter can
      // distinguish "no second screen" / "no cursor exits" from "this
      // browser/device can't tell". emitEvent retries transient drops — if
      // this one POST is lost, every capability chip degrades to "unknown".
      //
      // FEAT-25: with activity monitoring off none of it is probed or sent —
      // the recruiter's page then says "off for this test", not "unknown".
      const activityProbes = () => {
        const secondScreen = probeSecondScreen();
        const cursorTracking = probeCursorTracking();
        // Three-way capability, not two. A browser without screen.isExtended
        // (Firefox, Safari, or any non-secure context) can still run the
        // indirect placement check in anticheat/displays.ts, so it reports
        // "limited" — "we checked continuously and saw nothing, but a monitor
        // this window never visited would be invisible to us". Only a browser
        // that can't even do that is "unsupported". Without this split every
        // Firefox attempt read "Browser can't report", which is now false.
        const secondScreenSupport =
          secondScreen.support === "supported"
            ? "supported"
            : canProbeDisplayPlacement()
              ? "limited"
              : "unsupported";
        // EF-4: captured on the setup step (re-probed here in case the step
        // was skipped by a practice return). Context for the recruiter only.
        const probe = extensionProbeRef.current ?? probeExtensionTraces();
        return {
          second_screen_support: secondScreenSupport,
          second_screen_at_start:
            secondScreen.value || probeDisplayPlacement() === "non_primary",
          cursor_tracking_support: cursorTracking.support,
          extension_traces: probe.traces,
          foreign_dom_nodes: probe.foreignNodes,
          // FEAT-38: what the browser says about itself (webdriver,
          // headless traits), only where the context is available. The
          // server adds `signed_agent` from the request header. Context
          // for the attempt page — never a flag.
          ...(interactionAvailable
            ? { automation_traits: probeAutomationTraits() }
            : {}),
        } as const;
      };
      emitEvent(token, result.attempt_id, {
        type: "attempt_started",
        form_factor: probeFormFactor(),
        ...(checks.activity ? activityProbes() : {}),
      });
      if (checks.camera) {
        setStep({ kind: "camera", attemptId: result.attempt_id, requesting: false });
      } else {
        // FEAT-25: no camera step. Release a stream the Welcome step's
        // optional camera check may have opened, so no light stays on.
        disposeActiveStream();
        setStep({ kind: "ready", attemptId: result.attempt_id });
      }
    } catch (err) {
      // EP-20 first: a knock-off answer is a terminal outcome, not a form
      // error the candidate could "fix" by resubmitting.
      const declined = screeningDeclinedMessage(err);
      if (declined) {
        setStep({ kind: "declined", message: declined });
        return;
      }
      // Any other refusal from the screening step is about the form (the
      // identity mismatch, CP-16's bad email, a stale-config reload message):
      // go back to it so the candidate can read the message where it applies.
      // EP-20: on a screening test, `runStart` is only ever reached from the
      // screening step, and the one failure that step can really produce is the
      // recruiter editing the questions under the candidate. `preview` is
      // cached for a minute, so without this refetch the next Continue re-asks
      // the SAME stale questions and earns the same 400 — forever, until the
      // candidate works out that "please reload the page" was literal. Keyed on
      // the test, never on the message text (memory:
      // project_candidate_error_classifier); a refetch is harmless for the
      // other refusals, which leave the questions unchanged.
      if (screeningQuestions.length > 0) {
        void preview.refetch();
      }
      // EF-3: the identity step is the other place /start can run from.
      // Without this a refusal there (a mismatched invitation, a bad email)
      // left the candidate on a card with no field to fix.
      setStep((prev) =>
        prev.kind === "screening" || prev.kind === "identity"
          ? { kind: "form" }
          : prev,
      );
      // Classified off ApiError.status/detail, never by scraping the message
      // (memory: project_candidate_error_classifier). A structured detail —
      // the invitation-identity 409, CP-16's email_undeliverable 400 — used to
      // reach the candidate as raw JSON through the old "detail" regex.
      const described = describeStartError(err);
      if (described.field === "email") {
        setEmailError(described.message);
        emailInputRef.current?.focus();
      } else {
        setError(described.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // EF-2: `resume` rides the whole chain when this is a paused attempt coming
  // back — the same camera → ready steps, but the Begin gesture then calls
  // /resume for `resume.position` instead of opening question 1.
  // CP-18: land on the break screen exactly as a stale tab does (Question.tsx
  // rebuilds it from the 409's bounds) — the clock stays stopped until the
  // candidate presses "Begin Section N" there, which also re-enters
  // fullscreen on its own click.
  const goToBreak = (info: BreakReturnInfo) => {
    navigate(`/t/${token}/section-review`, {
      replace: true,
      state: {
        sectionStart: info.sectionStart,
        sectionEnd: info.sectionEnd,
        sectionIndex: info.sectionIndex,
        isLast: info.isLast,
        paused: true,
        sharedClock: info.sharedClock,
        breakBetweenSections: true,
      },
    });
  };

  // CP-18: with `breakReturn` the camera step leads to the break screen
  // instead of `ready` (see the Step note).
  const requestCamera = async (
    attemptId: string,
    resume?: ResumeCardInfo,
    breakReturn?: BreakReturnInfo,
  ) => {
    setStep({ kind: "camera", attemptId, requesting: true, resume, breakReturn });
    try {
      const stream = await requestCameraStream();
      // CP-14: the device label rides every camera_started so the server can
      // tell a later re-acquire (resume, unplug) on a DIFFERENT camera apart.
      emitEvent(token, attemptId, {
        type: "camera_started",
        device_label: describeCameraDevice(stream),
      });
      // OPS-28 #13: the flag the recruiter's "Camera permission" chip reads.
      // One failed POST (a blip) used to be swallowed as "the event log has
      // camera_started" — but the chip read the FLAG, so the recruiter saw
      // "Off" for a camera that ran. Bounded retries in the background (the
      // candidate's step never waits on it), Sentry when they are spent; the
      // detail page now also falls back to the camera_started event.
      void markCameraEnabledDurably(token, attemptId);
      if (breakReturn) {
        goToBreak(breakReturn);
        return;
      }
      // Consent given + camera live. Hand off to the "ready" step, whose
      // "Begin assessment" button is the fresh user gesture that enters
      // fullscreen and opens Q1 — see beginAssessment below.
      setStep({ kind: "ready", attemptId, resume });
    } catch (err) {
      const reason: CameraDenyReason =
        (err as CameraStreamError)?.reason ?? "error";
      emitEvent(token, attemptId, { type: "camera_permission_denied", reason });
      setStep({ kind: "denied", attemptId, reason, resume, breakReturn });
    }
  };

  // Final gesture before the timed test. It runs AFTER the camera permission
  // prompt has already resolved (camera step), so requestFullscreen() can bank
  // the activation token without the prompt stealing it — the exact failure
  // mode that got early fullscreen entry reverted (see the comment in onSubmit).
  // Fullscreen targets document.documentElement, so it survives the client-side
  // route change into Question 1, which then mounts already-fullscreen (its
  // blocking overlay stays dormant unless the candidate later exits). On a
  // browser without the Fullscreen API we just navigate; Question 1 logs
  // fullscreen_unavailable: not_supported as before.
  const beginAssessment = async (attemptId: string, resume?: ResumeCardInfo) => {
    // requestFullscreen() is invoked synchronously here (before any await), so
    // it banks the click's activation token. We await the transition before
    // navigating so Question 1 mounts reliably already-fullscreen (no one-frame
    // overlay flash); a rejection (unsupported/denied) just falls through to
    // navigate, and Question 1 handles the not-fullscreen path as before.
    // FEAT-25: read synchronously, so the click's activation token is still
    // fresh when fullscreen IS wanted.
    if (frozenChecks().fullscreen && isFullscreenSupported()) {
      try {
        await requestFullscreen();
      } catch {
        // Rejected (iframe / permissions-policy / denied) — proceed anyway.
      }
    }
    // A fresh /start: question 1. (`resumeAttemptId` is the handle found at
    // MOUNT, so the normal flow — whose id is saved later, at /start — never
    // matches it.)
    if (!resume && attemptId !== resumeAttemptId) {
      navigate(`/t/${token}/question/1`, { replace: true });
      return;
    }
    // EF-2: the clock restarts HERE — camera back, fullscreen requested, the
    // candidate's own click — never earlier. The server measures the pause
    // and shifts the deadline; the response says where to land.
    // A RUNNING attempt (a reload, the link reopened) asks the server where
    // the candidate is instead; nothing is written. Paused or on a break, the
    // question it names answers its own 409 and rebuilds that screen.
    setResuming(true);
    try {
      const resp = resume
        ? await resumeAttempt(token, attemptId)
        : await fetchAttemptPosition(token, attemptId);
      navigate(`/t/${token}/question/${resp.position}`, { replace: true });
    } catch (err) {
      if (!resume) {
        // Classified like a question read, and for the same reason: only a
        // 404 that names the ATTEMPT may drop the handle — a deploy-window 404
        // for a route an older machine lacks would otherwise lock out a live
        // attempt (memory: project_candidate_error_classifier).
        const action = classifyQuestionError(err);
        if (action === "done") {
          navigate(`/t/${token}/done`, { replace: true });
          return;
        }
        if (action === "clear-and-bounce") {
          clearAttemptId(token);
          setResumeAttemptId(null);
          setStep({ kind: "welcome" });
          return;
        }
        setResumeError("Could not resume the test. Please try again.");
        return;
      }
      const detail =
        err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { reason?: unknown; message?: unknown })
          : null;
      if (err instanceof ApiError && err.status === 410) {
        // The resume window closed (or the attempt was finished elsewhere):
        // a calm terminal card, in the server's words.
        clearAttemptId(token);
        setStep({
          kind: "resume_expired",
          message:
            typeof detail?.message === "string"
              ? detail.message
              : typeof err.detail === "string"
                ? err.detail
                : "This test can no longer be resumed. Please contact the recruiter.",
        });
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        // The stored handle is junk for this link (retake, erasure): drop it
        // and run the normal gate.
        clearAttemptId(token);
        setStep({ kind: "welcome" });
        return;
      }
      setResumeError("Could not resume the test. Please try again.");
    } finally {
      setResuming(false);
    }
  };

  if (step.kind === "unsupported") {
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md border-destructive/40 bg-destructive/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle
                className="h-5 w-5 text-destructive"
                aria-hidden="true"
              />
              <CardTitle as="h1" className="text-xl text-destructive">
                Browser not supported
              </CardTitle>
            </div>
            <CardDescription>
              This assessment requires a modern browser. The following
              capabilities are missing:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
              {step.missing.map((feature) => (
                <li key={feature}>
                  <span className="font-mono">{feature}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Recommended: the latest version of Chrome, Firefox, Edge, or
              Safari. Update your browser, then reload this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // EF-2: the resume window closed (or the attempt ended elsewhere) — a calm
  // terminal card in the server's words, never the red error box.
  if (step.kind === "resume_expired") {
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
              <Clock className="h-6 w-6 text-info" aria-hidden="true" />
            </div>
            <CardTitle as="h1" className="text-xl">
              This test can&rsquo;t be resumed
            </CardTitle>
            <CardDescription>{step.message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // EF-2: "Welcome back". The same camera → ready chain as a fresh start, so
  // the camera is re-acquired and fullscreen re-entered on the candidate's
  // own gestures before the clock restarts (on Begin, via POST /resume).
  if (step.kind === "resume") {
    const info = step.info;
    const meta = loadCandidateMeta(token);
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1" className="text-xl">
              Welcome back{meta?.candidate_name ? `, ${meta.candidate_name}` : ""}
            </CardTitle>
            <CardDescription>
              {meta?.test_name ? `${meta.test_name} is paused. ` : "Your test is paused. "}
              You&rsquo;ll continue on the question you paused on, with the
              clock exactly where you left it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {info.remainingSeconds !== null && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm">
                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">
                  Time remaining:{" "}
                  <span className="font-mono font-medium tabular-nums text-foreground">
                    {formatHMS(info.remainingSeconds * 1000)}
                  </span>
                </span>
              </div>
            )}
            {info.resumeBy && (
              <p className="text-xs text-muted-foreground">
                Resume by{" "}
                <span className="font-medium text-foreground">
                  {formatDateTime(info.resumeBy)}
                </span>
                . After that the test can no longer be reopened.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {/* FEAT-25: only the steps this attempt actually has. */}
              {resumeNextLine(frozenChecks())}
              The clock starts again only when you press Resume on the last
              step.
            </p>
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              className="w-full cursor-pointer min-h-11"
              onClick={() =>
                // FEAT-25: no camera to switch back on ⇒ straight to Resume.
                frozenChecks().camera
                  ? requestCamera(step.attemptId, info)
                  : setStep({ kind: "ready", attemptId: step.attemptId, resume: info })
              }
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Continue
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // CP-18 (Sheet #29): "Welcome back" for a test that is on a break. Nothing
  // here touches the clock — it is stopped, and stays stopped until "Begin
  // Section N" on the break screen. ⚠ Placeholder copy — the wording is
  // Emilia's.
  if (step.kind === "break_return") {
    const info = step.info;
    const meta = loadCandidateMeta(token);
    const nextSection = info.sectionIndex + 2;
    const wantsCamera = frozenChecks().camera;
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1" className="text-xl">
              Welcome back{meta?.candidate_name ? `, ${meta.candidate_name}` : ""}
            </CardTitle>
            <CardDescription>
              {meta?.test_name ? `${meta.test_name} is on a break. ` : "Your test is on a break. "}
              Section {info.sectionIndex + 1} is finished and saved, and the
              clock is stopped.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {info.remainingSeconds !== null && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm">
                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">
                  Time left for the rest of the test:{" "}
                  <span className="font-mono font-medium tabular-nums text-foreground">
                    {formatHMS(info.remainingSeconds * 1000)}
                  </span>
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {wantsCamera
                ? "Next you'll switch your camera back on, then you're back on your break screen. "
                : "Next you're back on your break screen. "}
              The clock starts again only when you press &ldquo;Begin Section{" "}
              {nextSection}&rdquo; there.
            </p>
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              className="w-full cursor-pointer min-h-11"
              onClick={() =>
                wantsCamera
                  ? setStep({
                      kind: "camera",
                      attemptId: step.attemptId,
                      requesting: false,
                      breakReturn: info,
                    })
                  : goToBreak(info)
              }
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Continue
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step.kind === "welcome") {
    return (
      <WelcomeStep
        preview={preview.data}
        loading={preview.isLoading}
        error={preview.isError}
        errorMessage={gateErrorMessage(preview.error)}
        // FEAT-16: the test rules come first, on every test.
        onContinue={() => setStep({ kind: "rules" })}
        onTryPractice={() => navigate(`/t/${token}/practice`)}
      />
    );
  }

  // FEAT-16: "Test rules" — what is allowed, what is not, what is monitored
  // (only the checks this test runs) and the consequence. An acknowledgement,
  // not a consent: nothing is ticked or recorded here, and the consent ticks
  // on the form are untouched.
  if (step.kind === "rules") {
    return (
      <TestRulesStep
        rules={testRules}
        // EF-4: the browser-setup step sits between the rules and the form.
        // FEAT-25: skipped when the recruiter switched every check off.
        onContinue={() => setStep({ kind: setupSkipped ? "form" : "setup" })}
      />
    );
  }

  // EF-4: "Before you start — browser setup". A web page CANNOT see or turn
  // off extensions (see anticheat/extensions.ts), so this is an instruction
  // plus an honour acknowledgement, recorded on the attempt at /start. The
  // probe runs here too, so what it saw at this moment rides attempt_started.
  if (step.kind === "setup" && !setupSkipped) {
    return (
      <BrowserSetupStep
        confirmed={extensionsConfirmed}
        onConfirmedChange={setExtensionsConfirmed}
        onContinue={() => {
          // FEAT-25: the probe belongs to activity monitoring.
          if (previewChecks.activity) {
            extensionProbeRef.current = probeExtensionTraces();
          }
          setStep({ kind: "form" });
        }}
      />
    );
  }

  if (step.kind === "screening") {
    return (
      <ScreeningStep
        questions={screeningQuestions}
        answers={screening}
        onAnswer={(key, value) =>
          setScreening((prev) => ({ ...prev, [key]: value }))
        }
        onBack={() => setStep({ kind: "form" })}
        onContinue={() => void startOrIdentity()}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (step.kind === "identity") {
    return (
      <IdentityStep
        status={identity.status}
        url={identity.url}
        opening={identityOpening}
        openError={identityOpenError}
        onOpen={() => void openIdentity()}
        onContinue={() => void runStart(false)}
        onDecline={() => void runStart(true)}
        onBack={() =>
          setStep(
            screeningQuestions.length > 0
              ? { kind: "screening" }
              : { kind: "form" },
          )
        }
        submitting={submitting}
        error={error}
      />
    );
  }

  if (step.kind === "declined") {
    return <ScreeningDeclinedCard message={step.message} />;
  }

  if (step.kind === "camera" || step.kind === "denied") {
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1" className="text-xl">
              {step.kind === "denied" ? "Camera access is required" : "Camera access"}
            </CardTitle>
            <CardDescription>
              {step.kind === "denied"
                ? denyCopy(step.reason)
                : "We need camera access to confirm one person is taking the test. The recruiter is notified if the camera is turned off during the assessment."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm">
              {step.kind === "denied" ? (
                <CameraOff className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              ) : (
                <Camera className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <span className="text-muted-foreground">
                {step.kind === "denied"
                  ? step.reason === "denied" || step.reason === "revoked"
                    ? "Camera permission was not granted."
                    : "Your browser allowed the camera, but it could not be started."
                  : resumeAttemptId
                    ? /* A reload / the link reopened while the test runs:
                         /preview is skipped here, so the snapshot schedule is
                         unknown — say nothing about what is stored (the
                         candidate read the full notice before /start).
                         Placeholder copy (wording is Emilia's). */
                      "Your test is still running. Switch the camera back on to continue — your browser will ask permission to use it."
                  : snaps
                    ? /* OPS-15: legal supplies the final wording */
                      `A photo is taken every ${snaps.interval_s} seconds while the test runs and kept for ${snaps.retention_days} days, visible only to the recruiting team. Your browser will ask permission to use the camera.`
                    : "We don't record or store video — we only check that one face is visible. Your browser will ask permission to use the camera."}
              </span>
            </div>
            <PermissionPromptHint waiting={step.kind === "camera" && step.requesting} />
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              className="w-full cursor-pointer min-h-11"
              disabled={step.kind === "camera" && step.requesting}
              onClick={() =>
                requestCamera(step.attemptId, step.resume, step.breakReturn)
              }
            >
              {step.kind === "camera" && step.requesting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Waiting for permission…
                </>
              ) : step.kind === "denied" ? (
                "Try again"
              ) : (
                "I consent — enable camera"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step.kind === "ready") {
    const resume = step.resume;
    // FEAT-25: say only what this attempt has (the frozen checks).
    const ready = frozenChecks();
    return (
      <div className="flex min-h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1" className="text-xl">You're all set</CardTitle>
            <CardDescription>
              {ready.camera ? "Your camera is active. " : ""}
              {resume
                ? `The test reopens ${ready.fullscreen ? "in fullscreen " : ""}on the question you paused on, and the clock restarts the moment you click below.`
                : `The assessment ${ready.fullscreen ? "opens in fullscreen and " : ""}runs against a timer — click below when you're ready to begin.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(ready.camera || ready.fullscreen) && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-sm">
                <CheckCircle2
                  className="h-4 w-4 shrink-0 text-success"
                  aria-hidden="true"
                />
                <span className="text-muted-foreground">
                  {ready.camera ? "Camera enabled. " : ""}
                  {ready.fullscreen
                    ? "Stay in fullscreen for the whole assessment — leaving it is recorded and shared with the recruiter."
                    : ""}
                </span>
              </div>
            )}
            {resumeError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {resumeError}
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              className="w-full cursor-pointer min-h-11"
              disabled={resuming}
              onClick={() => void beginAssessment(step.attemptId, resume)}
            >
              {resuming ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resuming…
                </>
              ) : resume ? (
                "Resume assessment"
              ) : (
                "Begin assessment"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <form onSubmit={onSubmit}>
          <CardHeader>
            <CardTitle as="h1" className="text-xl">
              {preview.data ? `Sign in to start “${preview.data.test_name}”` : "Sign in"}
            </CardTitle>
            <CardDescription>
              {/* EF-9: a questionnaire has no clock at all. FEAT-11: a mixed
                  test does — saying otherwise here would be found out on the
                  first question. */}
              {preview.data?.soft_skills_only
                ? "There’s no time limit — take the time you need. One attempt per candidate."
                : screeningQuestions.length > 0
                  ? // EP-20: the button reads "Continue" — screening comes next.
                    "Next, a few quick questions from the recruiter. Your timer starts after them. One attempt per candidate."
                  : "Your timer starts the moment you click “Start the test”. One attempt per candidate."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isInvitationLink && (
              <div className="rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                Your details are pre-filled from your invitation. To change
                them, contact the recruiter.
              </div>
            )}
            {/* B14 (2026-09-12 browser QA): `readOnly`, never `disabled`.
                Disabled rendered the invitation's real values in the browser's
                grey, indistinguishable from the "Ada Lovelace" placeholder the
                shared-link variant shows in the same slot — so a candidate
                could not tell which email their result would be tied to. It
                also dropped both fields out of the tab order and left them
                inconsistently announced. readOnly keeps them focusable and
                readable, and the lock icon + muted fill say why they cannot be
                edited. Nothing changes on the wire: the form submits React
                state, which is filled from the invitation either way. */}
            <div className="space-y-2">
              <Label htmlFor="candidate-name" className="flex items-center gap-1.5">
                Full name
                {isInvitationLink && (
                  <Lock
                    className="h-3 w-3 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
              </Label>
              <Input
                id="candidate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ada Lovelace"
                required
                autoComplete="name"
                readOnly={isInvitationLink}
                className={cn(isInvitationLink && "bg-muted/40 text-foreground")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="candidate-email" className="flex items-center gap-1.5">
                Email
                {isInvitationLink && (
                  <Lock
                    className="h-3 w-3 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
              </Label>
              <Input
                id="candidate-email"
                ref={emailInputRef}
                type="email"
                maxLength={EMAIL_MAX_LENGTH}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                onBlur={() => setEmailTouched(true)}
                // Fires exactly when `type=email` refuses the submit, so the
                // message in the DOM appears with the native bubble rather
                // than instead of it.
                onInvalid={() => setEmailTouched(true)}
                placeholder="ada@example.com"
                required
                autoComplete="email"
                readOnly={isInvitationLink}
                className={cn(isInvitationLink && "bg-muted/40 text-foreground")}
                aria-invalid={shownEmailError !== null}
                aria-describedby={
                  shownEmailError ? "candidate-email-error" : undefined
                }
              />
              {shownEmailError && (
                <p
                  id="candidate-email-error"
                  className="text-xs text-destructive"
                >
                  {shownEmailError}
                </p>
              )}
            </div>
            {/* CP-12: the recruiter's extra sign-in fields. Editable even on a
                per-invitation link — only name + email are locked to the
                invitation. Errors show once a field has been left, and Submit
                stays disabled until every field passes (see canSubmit). */}
            {intakeFields.length > 0 && (
              <div className="space-y-4 border-t border-border pt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  About you
                </p>
                {intakeFields.map((f) => {
                  const attrs = intakeInputAttrs(f.type);
                  const value = intake[f.key] ?? "";
                  const fieldError = intakeTouched.has(f.key)
                    ? intakeValueError(f, value)
                    : null;
                  const inputId = `intake-${f.key}`;
                  const errorId = `intake-${f.key}-error`;
                  return (
                    <div key={f.key} className="space-y-2">
                      <Label htmlFor={inputId}>
                        {f.label}
                        {!f.required && (
                          <span className="font-normal text-muted-foreground">
                            {" "}
                            (optional)
                          </span>
                        )}
                      </Label>
                      <Input
                        id={inputId}
                        type={attrs.type}
                        inputMode={attrs.inputMode}
                        autoComplete={attrs.autoComplete}
                        placeholder={attrs.placeholder}
                        value={value}
                        required={f.required}
                        onChange={(e) =>
                          setIntake((prev) => ({
                            ...prev,
                            [f.key]: e.target.value,
                          }))
                        }
                        onBlur={() =>
                          setIntakeTouched((prev) => new Set(prev).add(f.key))
                        }
                        aria-invalid={fieldError !== null}
                        aria-describedby={fieldError ? errorId : undefined}
                      />
                      {fieldError && (
                        <p id={errorId} className="text-xs text-destructive">
                          {fieldError}
                        </p>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground">
                  These details are requested by the recruiter and shared only
                  with their team.
                </p>
              </div>
            )}
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Before you start, please confirm
              </p>
              {/* UX-8 — state it where the candidate is actually deciding,
                  rather than only in the privacy notice they'd have to open, or
                  on the camera step that comes AFTER they've already consented.
                  Same claims as /privacy-notice §2 (legal v1.6: the live image
                  is never stored, photos only where the test takes them, no
                  facial identification), compressed to one plain sentence. */}
              {/* FEAT-25: a camera sentence only where a camera is used. The
                  consent ticks below are legal copy and stay as they are. */}
              {previewChecks.camera && (
              <div className="flex items-start gap-2 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                <Camera
                  className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                {snaps ? (
                  /* OPS-15: legal supplies the final wording */
                  <span>
                    Your camera is checked live during the test to confirm one
                    person is present, and{" "}
                    <span className="font-medium text-foreground">
                      a photo is taken every {snaps.interval_s} seconds
                    </span>{" "}
                    (plus one when something unusual happens). The photos are
                    kept for {snaps.retention_days} days, seen only by the
                    recruiting team, and never analysed automatically — no
                    video is recorded, and we don&rsquo;t identify you from
                    your face.
                  </span>
                ) : (
                  <span>
                    Your camera is checked live during the test to confirm one
                    person is present.{" "}
                    <span className="font-medium text-foreground">
                      We don&rsquo;t record or store any video
                    </span>{" "}
                    — nothing is saved, and we don&rsquo;t identify you from your
                    face.
                  </span>
                )}
              </div>
              )}
              {identityRequired && (
                /* OPS-15: legal supplies the final wording. EF-3: the camera
                   sentence above stays true for the proctoring camera; THIS is
                   the one part of the process that compares a face with a
                   document, so it is said here, before the candidate agrees. */
                <div className="flex items-start gap-2 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                  <ShieldCheck
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>
                    This test also offers a separate{" "}
                    <span className="font-medium text-foreground">
                      identity check
                    </span>{" "}
                    on the next step: an identity document and a selfie, on our
                    verification partner&rsquo;s page. It is optional, and it
                    is the only part of this process that compares your face
                    with a document.
                  </span>
                </div>
              )}
              <div className="space-y-2">
                <ConsentCheckbox checked={agreed} onChange={setAgreed}>
                  I will complete this assessment on my own without using AI
                  tools or outside help.
                </ConsentCheckbox>
                <ConsentCheckbox
                  checked={consentProctoring}
                  onChange={setConsentProctoring}
                >
                  {snaps
                    ? /* OPS-15: legal supplies the final wording */
                      `I consent to proctoring during the test — camera monitoring and a photo every ${snaps.interval_s} seconds, kept for ${snaps.retention_days} days — in order to verify that the work is done independently.`
                    : "I consent to proctoring (camera + monitoring) during the test, in order to verify that the work is done independently."}
                </ConsentCheckbox>
                <ConsentCheckbox checked={acceptTerms} onChange={setAcceptTerms}>
                  I have read and accept the{" "}
                  <a
                    href="/privacy-notice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    privacy notice
                  </a>
                  .
                </ConsentCheckbox>
              </div>
            </div>
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full cursor-pointer min-h-11"
              disabled={submitting || !canSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Starting…
                </>
              ) : screeningQuestions.length > 0 || identityRequired ? (
                // EP-20 / EF-3: another step comes next, not the test.
                "Continue"
              ) : (
                "Start the test"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

// Turn a preview-fetch error into candidate-friendly copy. The thrown message
// is shaped `API <status>: <body>` (see api.ts throwIfError); for a 410 the
// body is `{"detail":"…"}` carrying the one-time/expiring-token reason
// ("expired" / "already been used" / "no longer valid"). Anything else falls
// back to the generic "link may be wrong" copy (null → caller uses default).
const UNKNOWN_LINK_MESSAGE =
  "We couldn't find a test for this link. If you received more than one email about this assessment, open the link in the most recent one — or ask the person who invited you to send a new link.";

function gateErrorMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const match = /^API (\d+):\s*([\s\S]*)$/.exec(err.message);
  // An unknown link: mistyped, or replaced by a newer email (a retake, or two
  // resends since). Point at the newest email rather than "refresh" — a
  // refresh can never fix it. Placeholder wording (Emilia's call).
  if (match?.[1] === "404") return UNKNOWN_LINK_MESSAGE;
  if (!match || match[1] !== "410") return null;
  try {
    const detail = (JSON.parse(match[2]) as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail) return detail;
  } catch {
    /* non-JSON body — fall through to generic copy */
  }
  return null;
}

/** Advisory notice for phones and tablets — NOT a gate.
 *
 * On a touch-only device iOS Safari has no element fullscreen, there is no
 * fine pointer for mouseleave, and Monaco is barely usable. Fullscreen
 * enforcement, cursor tracking and the second-screen check are therefore all
 * silently inert at once. The candidate keeps full control (the step's own
 * Continue button is the explicit opt-in) — this only makes the trade-off
 * visible, and `form_factor` on attempt_started tells the recruiter the same
 * thing so three structural zeros aren't read as exemplary behaviour.
 *
 * Deliberately not part of `browserCompat.ts`: that renders a terminal
 * "Browser not supported" card, and this is a device/form-factor observation,
 * not a browser gate.
 */
/** FEAT-25 — the "Welcome back" card's line about what comes before Resume:
 * only the steps this attempt has. Empty when it has neither. */
function resumeNextLine(checks: AnticheatConfig): string {
  if (checks.camera && checks.fullscreen) {
    return "Next, your camera is switched back on and the test reopens in fullscreen. ";
  }
  if (checks.camera) return "Next, your camera is switched back on. ";
  if (checks.fullscreen) return "Next, the test reopens in fullscreen. ";
  return "";
}

function TouchDeviceNotice({ checks }: { checks: AnticheatConfig }) {
  // Computed once — the form factor cannot change mid-mount.
  const [isTouch] = useState<boolean>(() => probeFormFactor() === "touch");
  if (!isTouch) return null;
  // FEAT-25: name only the checks this test runs (the screen-setup check is
  // part of activity monitoring).
  const cannot = [
    checks.fullscreen ? "keep the test in fullscreen" : null,
    checks.activity ? "check your screen setup" : null,
  ].filter(Boolean);
  return (
    <div
      className="flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3"
      role="note"
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">
        This assessment is designed for a laptop or desktop. On a phone or
        tablet the code editor is hard to use
        {cannot.length > 0 ? `, and we can't ${cannot.join(" or ")}` : ""}. You
        can continue, but a computer will give you a much better experience.
      </p>
    </div>
  );
}

function WelcomeStep({
  preview,
  loading,
  error,
  errorMessage,
  onContinue,
  onTryPractice,
}: {
  preview: PreviewResponse | undefined;
  loading: boolean;
  error: boolean;
  errorMessage?: string | null;
  onContinue: () => void;
  onTryPractice: () => void;
}) {
  // QA-2 (pre-ship 2026-10-10): the optional camera check above opens the
  // stream, and the tile then sat over "Start the test" on a phone.
  const tileShown = useCameraTileShown();
  // MVP 3.0 timing modes. per_section: show a per-section breakdown + sum the
  // section budgets as "Total time". per_question: each question is timed
  // separately (the preview carries no per-question budgets, and the whole-test
  // total is inert), so suppress the minutes figure and show a notice instead.
  type PreviewSection = {
    name: string;
    // null under a shared clock (CP-18): the section has no time of its own.
    time_limit_seconds: number | null;
    question_count: number;
  };
  const sections: PreviewSection[] =
    preview?.timing_mode === "per_section" && preview.sections
      ? (preview.sections as unknown as PreviewSection[])
      : [];
  const sectioned = sections.length > 0;
  const perQuestion = preview?.timing_mode === "per_question";
  // FEAT-12: the languages whose Run goes to our servers (the Needs row).
  const serverLanguageNames = (preview?.has_code ? (preview.code_languages ?? []) : [])
    .filter((l) => isServerLanguage(l))
    .map((l) => SANDBOX_NAME[languageOf([l])]);
  // CP-18: one time limit for the whole test — the sections carry none.
  const sharedClock = sectioned && Boolean(preview?.shared_clock);
  const breakBetweenSections = sectioned && Boolean(preview?.break_between_sections);
  const totalSeconds =
    sectioned && !sharedClock
      ? sections.reduce((sum, s) => sum + (s.time_limit_seconds || 0), 0)
      : (preview?.total_time_limit_seconds ?? 0);
  // EF-9: a questionnaire has no time limit (its hidden server bound is never
  // shown), so no minutes figure anywhere.
  // FEAT-11: a MIXED test does have one — it governs the questions, and the
  // trailing statements are clockless. `softSkillsOnly` is what suppresses
  // every clock line; `statements` is how many of `question_count` are
  // statements, so the two populations are never reported as one number.
  const softSkills = Boolean(preview?.soft_skills);
  const softSkillsOnly = Boolean(preview?.soft_skills_only);
  // FEAT-25: the checks this test runs today (the live /preview copy) — null
  // while /preview loads, so no camera line or check is shown on a guess.
  const checks = checksFromPreview(preview);
  const statements = preview?.statement_count ?? 0;
  const questionsOnly = (preview?.question_count ?? 0) - statements;
  const minutes =
    preview && !perQuestion && !softSkillsOnly
      ? Math.round(totalSeconds / 60)
      : null;

  return (
    <div
      className={cn(
        "flex min-h-full items-center justify-center bg-background p-6",
        tileShown && CAMERA_TILE_RESERVE,
      )}
    >
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Assessment
          </p>
          <CardTitle as="h1" className="text-2xl">
            {loading ? "Loading…" : preview ? `Welcome: ${preview.test_name}` : "Welcome"}
          </CardTitle>
          <CardDescription>
            A few things to know before you start.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {errorMessage
                ? errorMessage
                : "Couldn't load test details. Try refreshing the page — the link may be wrong."}
            </div>
          ) : (
            <>
              {checks && <TouchDeviceNotice checks={checks} />}
              <CompanyWelcomeBlock welcome={preview?.welcome} />
              <div className="grid grid-cols-2 gap-3">
                <StatLine
                  icon={FileText}
                  label={
                    softSkillsOnly
                      ? "Statements"
                      : statements > 0
                        ? "Questions & statements"
                        : preview && preview.question_count === 1
                          ? "Question"
                          : "Questions"
                  }
                  value={
                    !preview
                      ? "…"
                      : softSkillsOnly || statements === 0
                        ? String(preview.question_count)
                        : `${questionsOnly} + ${statements}`
                  }
                />
                {softSkillsOnly ? (
                  <StatLine icon={Clock} label="Time" value="No limit" />
                ) : perQuestion ? (
                  <StatLine icon={Clock} label="Timing" value="Per question" />
                ) : (
                  <StatLine
                    icon={Clock}
                    label={sectioned ? "Total time" : "Time"}
                    value={minutes !== null ? String(minutes) : "…"}
                    suffix="min"
                  />
                )}
              </div>
              {sectioned && (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {sharedClock
                      ? "Sections — one time limit for the whole test"
                      : "Sections — each is separately timed"}
                  </p>
                  <ol className="space-y-1.5 text-sm">
                    {sections.map((s, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between gap-3"
                      >
                        <span className="min-w-0 truncate text-foreground">
                          <span className="text-muted-foreground">{i + 1}.</span>{" "}
                          {s.name}
                        </span>
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          {s.question_count} Q
                          {s.time_limit_seconds != null
                            ? ` · ${Math.round(s.time_limit_seconds / 60)} min`
                            : ""}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p className="text-xs text-muted-foreground">
                    You'll work through these in order. Once you leave a section
                    you can't return to it.
                    {breakBetweenSections
                      ? " You can take a break between sections — the clock stops until you begin the next one."
                      : ""}
                  </p>
                </div>
              )}
              {perQuestion && (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Each question is individually timed
                  </p>
                  <p className="text-sm text-muted-foreground">
                    When a question&rsquo;s time runs out it locks and you move on
                    automatically — you can&rsquo;t go back. Answer each one before
                    its timer ends.
                  </p>
                </div>
              )}
              {/* EF-9: the IPIP instructions, verbatim from the server. Never
                  an instrument or trait name — candidates don't see those. */}
              {softSkills && preview?.likert_instructions && (
                <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    About this questionnaire
                  </p>
                  <p className="text-sm text-foreground">
                    {preview.likert_instructions}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    For each statement, choose how accurately it describes you,
                    from &ldquo;Very Inaccurate&rdquo; to &ldquo;Very
                    Accurate&rdquo;. Every statement needs an answer.
                    {statements > 0 && !softSkillsOnly
                      ? " The questionnaire comes after the test questions and has no time limit."
                      : ""}
                  </p>
                </div>
              )}
              <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4 text-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  What you'll need
                </p>
                <ul className="space-y-2 text-sm">
                  <Need
                    icon={CheckCircle2}
                    iconClass="text-success"
                    text="A modern browser (already verified)"
                  />
                  {checks?.camera && (
                    <Need
                      icon={Camera}
                      iconClass="text-muted-foreground"
                      text={
                        preview?.camera_snapshots
                          ? /* OPS-15: legal supplies the final wording */
                            `A working webcam — a photo is taken every ${preview.camera_snapshots.interval_s} seconds`
                          : "A working webcam — we check one face is on screen"
                      }
                    />
                  )}
                  <Need
                    icon={Wifi}
                    iconClass="text-muted-foreground"
                    text="A stable internet connection — you can test it below"
                  />
                  <Need
                    icon={Clock}
                    iconClass="text-muted-foreground"
                    text={
                      softSkillsOnly
                        ? "No time limit — answer at your own pace, one attempt only"
                        : statements > 0 && minutes !== null
                        ? `~${minutes} minutes for the questions, then a questionnaire with no time limit — one attempt only`
                        : minutes !== null
                        ? preview?.pause_enabled
                          ? `~${minutes} minutes on the clock — you only get one attempt`
                          : `~${minutes} uninterrupted minutes — you only get one attempt`
                        : preview?.pause_enabled
                          ? "Some time — one attempt only"
                          : "Some uninterrupted time — one attempt only"
                    }
                  />
                  {/* EF-2: say it BEFORE the clock starts. Config only — the
                      server freezes the same answer into the attempt. */}
                  {preview?.pause_enabled && (
                    <Need
                      icon={Play}
                      iconClass="text-muted-foreground"
                      text={`You can pause and come back later with this same link — within ${preview.pause_resume_window_hours} hours of pausing`}
                    />
                  )}
                  {/* Cheat-proof challenges: say it BEFORE the clock starts, so
                      the candidate doesn't first meet the twist on question 1
                      under time pressure. Config only — the token itself is
                      minted at /start and shown on each code question. */}
                  {/* FEAT-12: code that runs on our servers makes each Run
                      a round trip, on the candidate's clock — say so up front. */}
                  {serverLanguageNames.length > 0 && (
                    <Need
                      icon={Globe}
                      iconClass="text-muted-foreground"
                      text={`Your ${serverLanguageNames.join(" and ")} code runs on our servers each time you press Run tests — each run takes a few seconds and the clock keeps running`}
                    />
                  )}
                  {preview?.has_code && preview?.cheat_proof_challenges && (
                    <Need
                      icon={KeyRound}
                      iconClass="text-muted-foreground"
                      text="Code questions carry a personal ChallengeToken — each one tells you how to combine it with your answer"
                    />
                  )}
                </ul>
              </div>
              {/* FEAT-22: how the code editor works — the workspace's text or
                  the platform default. `/preview` sends it only for a test
                  with a coding question; the component renders nothing
                  otherwise. Read BEFORE the clock starts. */}
              <CodeEditorInstructions text={preview?.code_editor_instructions} />
              {checks?.camera && <CameraPreflight />}
              {/* CP-17 — advisory, never a gate: the Start button below does
                  not read its result. */}
              <ConnectionPreflight
                // FEAT-6: the same list the runtime preload derives — only
                // Python is the 14 MB download; an older server that sends
                // no `code_languages` means Python, as before.
                codeLanguages={
                  preview?.has_code
                    ? (preview.code_languages?.length ? preview.code_languages : ["python"])
                    : []
                }
              />
              <p className="text-xs text-muted-foreground">
                <Globe className="mr-1 inline h-3 w-3" aria-hidden="true" />
                {preview?.allow_back_navigation === false
                  ? "Your responses are saved as you go. Once you submit a question you can't change that answer, but you can review unsubmitted ones before final submission."
                  : "Your responses are saved as you go. You can go back to review and change any answer before final submission."}
              </p>
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            type="button"
            variant="outline"
            className="w-full cursor-pointer min-h-11 sm:flex-1"
            disabled={loading || error}
            onClick={onTryPractice}
          >
            Try practice first
          </Button>
          <Button
            type="button"
            className="w-full cursor-pointer min-h-11 sm:flex-1"
            disabled={loading || error}
            onClick={onContinue}
          >
            Start the test
          </Button>
        </CardFooter>
      </Card>
      <CameraPreview />
    </div>
  );
}

/** EF-1 — a greeting from the RECRUITING company (never from us), shown once
 * on the Welcome step before the clock starts. Config + marketing content
 * only: `/preview` has already resolved the per-test opt-out and the plan, so
 * this renders whatever it was handed and nothing when handed nothing.
 *
 * The video is SAME-ORIGIN (`/api/t/<token>/welcome-video`): an embedded
 * YouTube/Vimeo player would put a third party's cookies on candidates and
 * break the zero-cross-origin flow. No autoplay — the camera permission
 * prompt comes later and must not fight a playing video for the user's
 * attention — and it never gates the Start button.
 */
function CompanyWelcomeBlock({
  welcome,
}: {
  welcome: PreviewResponse["welcome"] | undefined;
}) {
  if (!welcome || (!welcome.video_url && !welcome.message)) return null;
  const headingId = "company-welcome-heading";
  return (
    <section
      aria-labelledby={headingId}
      className="space-y-3 rounded-md border border-border bg-muted/30 p-4"
    >
      <p
        id={headingId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        A message from {welcome.company_name}
      </p>
      {welcome.video_url && (
        // The recruiter's own words to the candidate — no captions exist to
        // attach, so the message below is the text alternative.
        <video
          controls
          playsInline
          preload="metadata"
          className="aspect-video w-full rounded-md bg-black"
          aria-label={`Welcome video from ${welcome.company_name}`}
        >
          <source
            src={welcome.video_url}
            type={welcome.video_type ?? undefined}
          />
          Your browser can&rsquo;t play this video.
        </video>
      )}
      {welcome.message && (
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
          {welcome.message}
        </p>
      )}
    </section>
  );
}

function StatLine({
  icon: Icon,
  label,
  value,
  suffix,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  suffix?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-border bg-card px-4 py-5 text-center">
      <Icon
        className="h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <p className="flex items-baseline gap-1.5 font-mono leading-none text-foreground">
        <span className="text-3xl font-semibold tabular-nums">{value}</span>
        {suffix && (
          <span className="text-sm font-medium text-muted-foreground">
            {suffix}
          </span>
        )}
      </p>
      <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

function Need({
  icon: Icon,
  iconClass,
  text,
}: {
  icon: typeof Camera;
  iconClass: string;
  text: string;
}) {
  return (
    <li className="flex items-start gap-2">
      <Icon
        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${iconClass}`}
        aria-hidden="true"
      />
      <span className="text-foreground/90">{text}</span>
    </li>
  );
}

type PreflightState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "ok" }
  | { kind: "error"; reason: CameraDenyReason };

// Voluntary "Test your camera" disclosure shown on the Welcome step (B2,
// pre-ship 2026-10-25: it said "camera & mic", but no microphone is ever
// requested — `requestCameraStream` asks for video only).
// Lets candidates verify camera permission BEFORE they commit to the timed
// test, so a permission denial doesn't become a dead-end at the gate. Once
// granted the stream stays alive so the CameraPreview thumbnail bottom-right
// appears immediately (proof to the candidate that the camera works) and
// persists through the rest of the candidate flow until disposed at outro
// (practice) / Done (timed). The downstream camera step sees the alive
// singleton and short-circuits silently — no second prompt.
function CameraPreflight() {
  const [state, setState] = useState<PreflightState>({ kind: "idle" });

  const run = async (): Promise<void> => {
    setState({ kind: "checking" });
    try {
      await requestCameraStream();
      setState({ kind: "ok" });
    } catch (err) {
      const reason: CameraDenyReason =
        (err as CameraStreamError)?.reason ?? "error";
      setState({ kind: "error", reason });
    }
  };

  return (
    <Disclosure label="Test your camera (optional)" icon={Camera}>
      {state.kind === "idle" && (
        <>
          <p className="text-muted-foreground">
            Verify permission before the test starts. We'll re-ask just
            before the real assessment begins.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void run()}
            className="cursor-pointer"
          >
            Check camera
          </Button>
        </>
      )}
      {state.kind === "checking" && (
        <>
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Waiting for permission…
          </p>
          <PermissionPromptHint waiting />
        </>
      )}
      {state.kind === "ok" && (
        <p className="flex items-center gap-2 text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          Camera detected. We'll re-prompt right before the real test.
        </p>
      )}
      {state.kind === "error" && (
        <>
          <p className="text-destructive">{denyCopy(state.reason)}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void run()}
            className="cursor-pointer"
          >
            Try again
          </Button>
        </>
      )}
    </Disclosure>
  );
}

// K-B8 (pre-ship 2026-10-25): a camera permission request can stay pending
// for good (Chrome's quiet prompt is only an address-bar icon; a prompt can sit
// behind another window). The request is never cancelled — camera.ts shares
// one pending getUserMedia — so after a while the page says where to look.
// ⚠ Placeholder copy — the wording is Emilia's (owed.md).
function PermissionPromptHint({ waiting }: { waiting: boolean }) {
  const late = useStillWaiting(waiting);
  if (!late) return null;
  return (
    <p role="status" className="text-xs text-muted-foreground">
      Don&rsquo;t see a prompt? Look for the camera icon in your browser&rsquo;s
      address bar and allow it there.
    </p>
  );
}

function denyCopy(reason: CameraDenyReason): string {
  switch (reason) {
    case "denied":
      return "Open your browser's site settings, allow camera access for this page, then click Try again.";
    case "no_device":
      return "No working webcam was detected. Connect a webcam (any USB camera works), then click Try again.";
    case "in_use":
      // NotReadableError: the permission was granted, the device is busy or
      // blocked below the browser. A video call is the usual holder; on
      // Windows the OS-level camera privacy switch produces the same error.
      return "Another app is using your webcam — a video call (Teams, Zoom, Meet), OBS or another browser tab. Close it, then click Try again. On Windows, also check that Settings → Privacy & security → Camera allows apps to use the camera.";
    case "revoked":
      return "Camera permission was revoked. Re-allow it in your browser's site settings and click Try again.";
    case "error":
    default:
      return "We couldn't start your camera. Reload the page and try again.";
  }
}
